// Minimal i18n. A flat key -> string table per language, a `t(key, params)`
// lookup with {param} interpolation, and an English fallback for any missing
// key. The chosen language is persisted; English is the default. The Göktürk
// theme proper nouns (era / planet / enemy names, the opening lore) stay as the
// game's identity across every language; only the functional UI is translated.

import { en } from './en';
import { tr } from './tr';
import { es } from './es';
import { de } from './de';
import { fr } from './fr';
import { contentTr } from './content/tr';
import { contentEs } from './content/es';
import { contentDe } from './content/de';
import { contentFr } from './content/fr';

export type Lang = 'en' | 'tr' | 'es' | 'de' | 'fr';

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
];

const DICTS: Record<Lang, Record<string, string>> = { en, tr, es, de, fr };
const LANG_KEY = 'gokturk.lang';

let current: Lang = 'en';

/** Reads the saved language (or browser default), clamped to a supported one. */
export function initLang(): Lang {
  let lang: Lang | null = null;
  try {
    const saved = localStorage.getItem(LANG_KEY) as Lang | null;
    if (saved && DICTS[saved]) lang = saved;
  } catch {
    /* ignore */
  }
  if (!lang) {
    // First visit: take the browser language if we support it, else English.
    try {
      const nav = (navigator.language || 'en').slice(0, 2).toLowerCase() as Lang;
      if (DICTS[nav]) lang = nav;
    } catch {
      /* ignore */
    }
  }
  current = lang ?? 'en';
  return current;
}

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  if (!DICTS[lang]) return;
  current = lang;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* ignore */
  }
}

// Game content (card / skill / tech / artifact / building / synergy names and
// descriptions) lives in English in the data files; these per-language tables
// override it. English just uses the data string, so there is no `en` content map.
const CONTENT: Partial<Record<Lang, Record<string, string>>> = {
  tr: contentTr,
  es: contentEs,
  de: contentDe,
  fr: contentFr,
};

/** Translate a content string by key, falling back to the supplied English (the
 *  data file value) when there is no translation or the language is English. */
export function tc(key: string, english: string): string {
  if (current === 'en') return english;
  return CONTENT[current]?.[key] ?? english;
}

/** Translate a key, with optional {name} interpolation. Falls back to English,
 *  then to the raw key, so a missing translation never blanks the UI. */
export function t(key: string, params?: Record<string, string | number>): string {
  let s = DICTS[current][key] ?? en[key] ?? key;
  if (params) {
    for (const k of Object.keys(params)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(params[k]));
    }
  }
  return s;
}
